import { Component, ElementRef, forwardRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TeamsService } from '../../../../../modules/teams/services/teams.service';
import { MediaType } from '../../../../enums/media-type';
import { MediaAction } from '../../../../../core/uploader/media-action';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { ImageCropComponent } from '../../../../../core/infocard-images/image-crop/image-crop.component';

@Component({
  selector: 'mee-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrls: ['./upload-file.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UploadFileComponent),
      multi: true,
    },
  ],
})
export class UploadFileComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() fileName: string;
  fileUrl: string;
  isLoading = false;
  private teamsService = inject(TeamsService);
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  private dialog = inject(MatLegacyDialog);
  private destroy$ = new Subject<void>();
  @Input() buttonText = 'Upload File';
  @Input() mediaType: MediaType;
  @Input() mediaAction: MediaAction = MediaAction.Direct;
  @Input() hideRemoveAction = false;
  @ViewChild('fileInput') fileInput: ElementRef;

  ngOnInit(): void {
    this.matIconRegistry.addSvgIcon('file', this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/img/icons/file.svg'));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChange: any = () => {};
  onTouch: any = () => {};

  writeValue(value: string): void {
    this.fileUrl = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  onFileSelected(event: Event): void {
    this.isLoading = true;
    const file: File = (event.target as HTMLInputElement).files[0];

    if (file) {
      this.fileName = file.name;
      const formData = new FormData();
      formData.append('file', file);

      if (this.mediaType === MediaType.IssueReportCategoryImage) {
        this.dialog
          .open(ImageCropComponent, {
            data: file,
          })
          .afterClosed()
          .pipe(
            filter(data => !!data),
            tap(image => {
              formData.set('file', image, 'index.png');

              this.uploadSelectedFile(formData);
            }),
            takeUntil(this.destroy$),
          )
          .subscribe();

        return;
      }

      this.uploadSelectedFile(formData);
    }

    this.fileInput.nativeElement.value = '';
  }

  cancel(): void {
    this.isLoading = false;
    this.fileName = '';
    this.fileUrl = '';
    this.onChange(this.fileUrl);
  }

  onClick(fileInput: HTMLInputElement): void {
    this.onTouch();
    fileInput.click();
  }

  private uploadSelectedFile(file: FormData) {
    this.teamsService
      .uploadFile(file, this.mediaType, this.mediaAction)
      .pipe(
        tap(res => {
          this.fileUrl = res.Url;
          this.isLoading = false;
          this.onChange(this.fileUrl);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
